import { Alert, Box, Snackbar } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DirectShearUpload } from './DirectShearUpload';
import { DirectShearDataGrid } from './DirectShearDataGrid';
import { getReadings, uploadStageReadings, replaceStageReadings } from '../../../../services/directShearReadingService';
import type { ReadingsByStage, ParsedStageData } from '../../../../types/directShearReading';
import { isResultReading } from '../../../../utils/directShearReadings';

interface DirectShearResultEntryProps {
  generalKey: string;
  savedDilationRange?: number | null;
  /** Per-stage dilation windows keyed by stage number (BUG-2026-0211); null = never calculated. */
  stageDilationRanges?: Record<number, number | null>;
  onCalculated?: () => void;
  readOnly?: boolean;
}

export const DirectShearResultEntry = ({ generalKey, savedDilationRange, stageDilationRanges, onCalculated, readOnly }: DirectShearResultEntryProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [readingsByStage, setReadingsByStage] = useState<ReadingsByStage>({});
  const [activeStage, setActiveStageState] = useState(() => {
    const s = parseInt(searchParams.get('stage') || '1', 10);
    return s >= 1 && s <= 5 ? s : 1;
  });

  const setActiveStage = useCallback((stage: number) => {
    setActiveStageState(stage);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (stage === 1) next.delete('stage');
      else next.set('stage', String(stage));
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [loading, setLoading] = useState(true);
  const [uploadExpanded, setUploadExpanded] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success',
  });

  const loadReadings = useCallback(async () => {
    try {
      const data = await getReadings(generalKey);
      setReadingsByStage(data);
      const hasData = Object.values(data).some((arr) => arr.length > 0);
      setUploadExpanded(!hasData);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load readings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [generalKey]);

  useEffect(() => { loadReadings(); }, [loadReadings]);

  const handleUpload = useCallback(async (parsed: ParsedStageData) => {
    try {
      await uploadStageReadings(generalKey, parsed);
      setSnackbar({ open: true, message: `Stage ${parsed.stageNumber}: ${parsed.readings.length} rows uploaded`, severity: 'success' });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        // Auto-replace — user is explicitly uploading new files
        await replaceStageReadings(generalKey, parsed.stageNumber, parsed);
        setSnackbar({ open: true, message: `Stage ${parsed.stageNumber}: replaced with ${parsed.readings.length} rows`, severity: 'info' });
      } else {
        throw err;
      }
    }
    setReadingsByStage((prev) => ({ ...prev, [parsed.stageNumber]: new Array(parsed.readings.length).fill({}) }));
    setActiveStage(parsed.stageNumber);
  }, [generalKey]);

  const handleUploadComplete = useCallback(async () => {
    setUploadExpanded(false);
    await loadReadings();
  }, [loadReadings]);

  const handleDataChange = useCallback(() => { loadReadings(); onCalculated?.(); }, [loadReadings, onCalculated]);

  const stageCounts = Object.fromEntries(
    [1, 2, 3, 4, 5].map((s) => [s, readingsByStage[s]?.length || 0]),
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <DirectShearUpload
        expanded={uploadExpanded}
        onToggle={() => setUploadExpanded((p) => !p)}
        onUpload={handleUpload}
        onUploadComplete={handleUploadComplete}
        stageCounts={stageCounts}
      />

      <DirectShearDataGrid
        generalKey={generalKey}
        readings={(readingsByStage[activeStage] || []).filter(isResultReading)}
        activeStage={activeStage}
        onStageChange={setActiveStage}
        stageCounts={stageCounts}
        loading={loading}
        onDataChange={handleDataChange}
        savedDilationRange={savedDilationRange}
        stageDilationRanges={stageDilationRanges}
        readOnly={readOnly}
      />

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
